import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { VisitorType } from '../../types/orgConfig';
import type { OnSiteVisitor } from '../../services/api/touchlessService';

interface VisitorState {
  currentVisitor: VisitorType | null;
  visitorinfo: Record<string, unknown> | null;
  preregisterId: string | null;
  visitorTypeId: string | null;
  visitorTypeInfo: VisitorType | null;
  preFillNew: boolean | null;
  isPrefill: boolean | null;
  preFillData: Record<string, unknown> | null | undefined;
  repeatCheckin: RepeatCheckinContext | null;
  preRegisterData: Record<string, unknown> | null;
  formData: {
    fullName: string;
    email: string;
    phone: string;
    company: string;
    hostName: string;
    hostId: string;
    purpose: string;
  };
  currentStep: string;
  signatureImage: string;
  photoImage: string;
  documentSigned: boolean;
  orgTemplateIds: string[] | null;
  videoUrl: string;
  visitorFullName: string;
  idValidationFields: IdValidationFields;
  welcomeMessage: string;
  watchlistMessage: string;
  flowInfoFlags: FlowInfoFlags | null;
  // EN-4518: true only when checkIdValidationExpiry confirmed the visitor's prior
  // ID is still valid, so the capture step is dropped from the flow. Deliberately
  // kept out of FlowInfoFlags — visitorForm re-dispatches that object with only
  // its two known keys on every visitor-type change, which would wipe a third one.
  idValidationSkip: boolean;
  // EN-4854: true only when checkVideoExpiry confirmed the visitor's earlier watch
  // is still inside the reuse window, so the video step is dropped. Kept out of
  // FlowInfoFlags for the same reason as idValidationSkip.
  videoSkip: boolean;
  visitStatus: {
    message: string | null;
    status: string | null;
    visitId: string | null;
    [ key: string ]: unknown;
  };
  onSiteVisitors: OnSiteVisitor[] | null;
}

export interface FlowInfoFlags {
  isIdCapturingFlag: boolean;
  visitorPhotoFlag: boolean;
}

export interface IdValidationFields {
  idCaptureType: string;
  idValidatedModels: IdentityModel[];
  offenderValidatedModels: OffenderModel[];
}

/**
 * EN-4557 repeat check-in (contactless ?id= flow, cycle 2+).
 *
 * Non-null means: the visitor already completed a cycle on this invitation, so the server no
 * longer returns a DRAFT `visitInfoModel`. The app must POST a NEW visit carrying
 * `referenceVisitId = sourceVisitId`, and must NEVER PATCH -- a PATCH against a finished cycle
 * is refused with 409 cycle_already_completed. Contrast `preFillNew`, which means the opposite:
 * a DRAFT row exists and is the thing to PATCH.
 */
export interface RepeatCheckinContext {
  /** Deliberately not named `id`: it is a carry-forward source, not a patch target. */
  sourceVisitId: string;
  preRegisterVisitId: string;
  /** Guard: only send referenceVisitId when it matches the device site. */
  sourceSiteId?: string;
}

export interface IdentityModel {
  identityId: string;
  identityStatus: string;
}

export interface OffenderModel {
  offenderCheckId: string;
  offenderStatus: string;
}

const initialState: VisitorState = {
  currentVisitor: null,
  visitorinfo: null,
  preregisterId: null,
  visitorTypeId: null,
  visitorTypeInfo: null,
  preFillNew: null,
  isPrefill: false,
  preFillData: null,
  repeatCheckin: null,
  preRegisterData: null,
  formData: {
    fullName: '',
    email: '',
    phone: '',
    company: '',
    hostName: '',
    hostId: '',
    purpose: '',
  },
  currentStep: 'home',
  signatureImage: '',
  photoImage: '',
  documentSigned: false,
  orgTemplateIds: [],
  videoUrl: '',
  visitorFullName: '',
  idValidationFields: {
    idCaptureType: '',
    idValidatedModels: [],
    offenderValidatedModels: [],
  },
  welcomeMessage: '',
  watchlistMessage: '',
  flowInfoFlags: {
    isIdCapturingFlag: false,
    visitorPhotoFlag: false,
  },
  idValidationSkip: false,
  videoSkip: false,
  visitStatus: {
    message: null,
    status: null,
    visitId: null,
  },
  onSiteVisitors: null,
};

const visitorSlice = createSlice({
  name: 'visitor',
  initialState,
  reducers: {
    setCurrentVisitor: (state, action: PayloadAction<VisitorType | null>) => {
      state.currentVisitor = action.payload;
    },
    setVisitorinfo: (state, action: PayloadAction<Record<string, unknown> | null>) => {
      // Merge with existing visitorinfo instead of replacing, so multiple
      // updateVisitorInfo() calls accumulate fields (e.g., fullName + email + phone)
      if (action.payload === null) {
        state.visitorinfo = null;
      } else {
        state.visitorinfo = { ...state.visitorinfo, ...action.payload };
      }
    },
    setPreregisterId: (state, action: PayloadAction<string | null>) => {
      state.preregisterId = action.payload;
    },
    setVisitorTypeId: (state, action: PayloadAction<string | null>) => {
      state.visitorTypeId = action.payload;
    },
    setVisitorTypeInfo: (state, action: PayloadAction<VisitorType | null>) => {
      state.visitorTypeInfo = action.payload;
    },
    updateFormData: (state, action: PayloadAction<Partial<VisitorState['formData']>>) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    setCurrentStep: (state, action: PayloadAction<string>) => {
      state.currentStep = action.payload;
    },
    setSignatureImage: (state, action: PayloadAction<string>) => {
      state.signatureImage = action.payload;
    },
    setPhotoImage: (state, action: PayloadAction<string>) => {
      state.photoImage = action.payload;
    },
    setDocumentSigned: (state, action: PayloadAction<boolean>) => {
      state.documentSigned = action.payload;
    },
    setVideoUrl: (state, action: PayloadAction<string>) => {
      state.videoUrl = action.payload;
    },
    setVisitorFullName: (state, action: PayloadAction<string>) => {
      state.visitorFullName = action.payload;
    },
    setPreFillNew: (state, action: PayloadAction<boolean | null>) => {
      state.preFillNew = action.payload;
    },
    setIsPrefill: (state, action: PayloadAction<boolean | null>) => {
      state.isPrefill = action.payload;
    },
    setPreFillData: (state, action: PayloadAction<Record<string, unknown> | null>) => {
      state.preFillData = action.payload;
    },
    setRepeatCheckin: (state, action: PayloadAction<RepeatCheckinContext | null>) => {
      state.repeatCheckin = action.payload;
    },
    setPreRegisterData: (state, action: PayloadAction<Record<string, unknown> | null>) => {
      state.preRegisterData = action.payload;
    },
    setFlowInfoFlags: (state, action: PayloadAction<FlowInfoFlags | null>) => {
      state.flowInfoFlags = action.payload;
    },
    setIdValidationSkip: (state, action: PayloadAction<boolean>) => {
      state.idValidationSkip = action.payload;
    },
    setVideoSkip: (state, action: PayloadAction<boolean>) => {
      state.videoSkip = action.payload;
    },
    setOrgTemplateIds: (state, action: PayloadAction<string[] | null>) => {
      state.orgTemplateIds = action.payload;
    },
    setWelcomeMessage: (state, action: PayloadAction<string>) => {
      state.welcomeMessage = action.payload;
    },
    setWatchlistMessage: (state, action: PayloadAction<string>) => {
      state.watchlistMessage = action.payload;
    },
    setVisitStatus: (state, action: PayloadAction<VisitorState['visitStatus']>) => {
      state.visitStatus = action.payload;
    },
    setIdValidationFields: (state, action: PayloadAction<IdValidationFields>) => {
      state.idValidationFields = action.payload;
    },
    setOnSiteVisitors: (state, action: PayloadAction<OnSiteVisitor[] | null>) => {
      state.onSiteVisitors = action.payload;
    },
    resetVisitor: () => {
      return initialState;
    },
  },
});

export const {
  setCurrentVisitor,
  setVisitorinfo,
  setPreregisterId,
  setVisitorTypeId,
  updateFormData,
  setCurrentStep,
  setSignatureImage,
  setPhotoImage,
  setDocumentSigned,
  setVideoUrl,
  setVisitorFullName,
  setPreFillNew,
  setIsPrefill,
  setPreFillData,
  setRepeatCheckin,
  setPreRegisterData,
  setVisitorTypeInfo,
  setFlowInfoFlags,
  setIdValidationSkip,
  setVideoSkip,
  setOrgTemplateIds,
  setWelcomeMessage,
  setWatchlistMessage,
  setVisitStatus,
  setIdValidationFields,
  setOnSiteVisitors,
  resetVisitor,
} = visitorSlice.actions;

export default visitorSlice.reducer;